import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";

import axios from 'axios'
import * as _ from "lodash"
import {am4Settings} from "@/mixins/am4charts/am4Settings"
import {SeriesColor} from "@/mixins/am4charts/SeriesColor";
import {SeriesDataLoader} from "@/mixins/am4charts/SeriesDataLoader"

const COLOR_LEFT = am4core.color("#3d81f7")
const COLOR_RIGHT = am4core.color("#1c4019")

const REPLACE = 0
const PREPEND = 1
const APPEND = 2

export const Series = class {
    constructor(company, chart, id, sideAxis, scalingProfile, start = null, end = null, axisPosition = am4Settings.AXIS_LEFT, loadBackFillIntervals = false) {
        this.create(company, chart, id, sideAxis, scalingProfile, start, end, axisPosition, loadBackFillIntervals)
    }

    /**
     * Add Series to Charts list of series w/out showing the series.
     * - While Adding series ensure that the series is not duplicated
     */
    setupSeries(shouldShow = true) {
        if (!this.isSetupValid()) {
            return
        }

        if (!this.isAm4SeriesReady()) {
            this.setupAm4Series()
        }

        if (shouldShow && !this.visible) {
            this.show()

            let color = SeriesColor.nextColor()
            this.color = color
            this.am4Series.stroke = color
            this.am4Series.fill = color
        }
        else if (!this.visible) {
            this.hide()
        }

        let seriesList = this.seriesChart.series
        if (seriesList.contains(this.am4Series)) {
            return
        }

        this.seriesChart.series.push(this.am4Series)
        this.setupBullets()
        this.setupTooltip()

        this.setAxis()
    }

    getSeries() {
        return this.am4Series
    }

    getYAxis() {
        if (_.isNil(this.sideAxis)) {
            return
        }

        return this.sideAxis.am4Axis
    }

    setAxisLeft() {
        this.axisPosition = am4Settings.AXIS_LEFT
        this.color = COLOR_LEFT
        this.setAxis()
    }

    setAxisRight() {
        this.axisPosition = am4Settings.AXIS_RIGHT
        this.color = COLOR_RIGHT
        this.setAxis()
    }

    setAxis() {
        if (_.isNil(this.sideAxis)) {
            console.error(`Series@setAxis - Side Axis is not set. Series: ${this.name}(${this.id})`)
            return
        }

        this.am4Series.yAxis = this.seriesChart.yAxes.getIndex(this.sideAxis.axisIndex)

        this.am4Series.stroke = this.color
        this.am4Series.fill = this.color

        this.setupThresholds()
    }

    setSideAxis(axis) {
        this.sideAxis = axis
    }

    setDateAxis(axis) {
        this.dateAxis = axis
    }

    /**
     * Remove the series from the chart list and code it to be hidden
     */
    removeSeries() {
        if (!this.isSetupValid()) {
            return
        }

        this.hide()
        this.reset()
        this.seriesChart.series.removeValue(this.am4Series)
    }

    hide() {
        if (!this.isSetupValid()) {
            return
        }

        if (this.isAm4SeriesReady()) {
            this.am4Series.visible = false
        }
        _.each(this.am4ThresholdAxisRanges, threshold => {
            if (!threshold.isDisposed()) {
                threshold.hide()
            }
        })
        this.visible = false
        // this.seriesDataLoader.reset()
    }

    show() {
        if (!this.isSetupValid()) {
            return
        }

        this.am4Series.visible = true
        this.visible = true
        _.each(this.am4ThresholdAxisRanges, threshold => {
            if (!threshold.isDisposed()) {
                threshold.show()
            }
        })

        this.am4Series.baseInterval = this.dateAxis.baseInterval
        this.seriesDataLoader.start()
    }

    isVisible() {
        return this.visible
    }

    reset() {
        this.backFillIntervals = []

        if (this.isAm4SeriesReady()) {
            this.am4Series.data = []
            this.am4Series.bullets.clear()
            this.am4Series.tooltip.label.adapter.remove('textOutput')
            this.dispose()
        }
        this.am4ThresholdAxisRanges = {}
        if (! _.isNil(this.getYAxis())) {
            this.getYAxis().axisRanges.clear()
        }
        this.sideAxis = null
    }

    setupAm4Series() {
        this.dispose()

        if(this.scalingProfile && this.scalingProfile.has_text_labels_on_graph){
            this.am4Series = new am4charts.StepLineSeries()
        } else {
            this.am4Series = new am4charts.LineSeries()
        }
        if (!_.isNil(this.seriesChart)) {
            this.am4Series.yAxis = this.sideAxis.getAm4Axis()
        }
        this.setupGroupingAdapter()
        this.am4Series.dataFields.dateX = 'date'
        this.am4Series.dataFields.valueY = `value`
        this.am4Series.strokeWidth = 2
        this.am4Series.connect = true
        this.am4Series.stroke = this.color
        this.am4Series.fill = this.color
    }

    setupBullets() {
        if (!this.isSetupValid()) {
            return
        }

        if (_.isNil(this.am4Series.bullets)) {
            return
        }

        this.am4Series.bullets.clear()
        let bullets = this.am4Series.bullets.push(new am4core.Circle())
        bullets.radius = 3
        bullets.propertyFields.fill = 'backgroundColor'
        bullets.propertyFields.stroke = 'backgroundColor'

        bullets.adapter.add('disabled', param => {
            return this.am4Series.data.length === 0
        })
    }

    setupTooltip() {
        if (!this.isSetupValid()) {
            return
        }

        this.am4Series.tooltipText = `tooltip`
        this.am4Series.tooltip.label.adapter.add("textOutput",
            (text, target, key) => {
                if (target.dataItem && target.dataItem.dataContext) {
                    return target.dataItem.dataContext[`tooltip`]
                }
                return text;
            }
        );

        this.am4Series.tooltip.pointerOrientation = "horizontal"
        this.am4Series.tooltip.background.cornerRadius = 20
        this.am4Series.tooltip.background.fillOpacity = 1
        this.am4Series.tooltip.background.propertyFields.fill = 'backgroundColor'
        this.am4Series.tooltip.background.propertyFields.stroke = 'backgroundColor'
        this.am4Series.tooltip.label.padding(12,12,12,12)
    }

    setupThresholds() {
        if (_.isNil(this.getYAxis())) {
            return
        }

        let alarmInfo = this.getAlarmInfo()
        let alarmValues = {}
        this.setupThresholdRange(alarmInfo.alarm, am4Settings.COLOR_ALARM, alarmValues)
        this.setupThresholdRange(alarmInfo.caution, am4Settings.COLOR_CAUTION, alarmValues)

        return alarmValues
    }

    clearThresholds() {
        if (!this.isSetupValid()) {
            return
        }

        let valueAxis = this.getYAxis()
        this.am4Series.axisRanges.clear()
        valueAxis.axisRanges.clear()
        this.am4ThresholdAxisRanges = []
    }

    setupThresholdRange(values, color, alarmValues) {
        let valueAxis = this.getYAxis()

        Object.keys(values).forEach(key => {
            let item = values[key]
            if (item === null) {
                return
            }
            let range = valueAxis.axisRanges.create();
            range.value = item;
            range.grid.stroke = color
            range.grid.strokeWidth = 2;
            range.grid.strokeOpacity = 1;
            if (this.axisPosition === am4Settings.AXIS_RIGHT) {
                range.grid.strokeDasharray = '5,4'
            }

            this.am4ThresholdAxisRanges.push(range)

            if (key === 'high_alarm'){
                alarmValues.highAlarmValue = item;
            } else if (key === 'low_alarm'){
                alarmValues.lowAlarmValue = item;
            }
            else if (key === 'high_caution'){
                alarmValues.highCautionValue = item;
            } else if (key === 'low_caution'){
                alarmValues.lowCautionValue = item;
            }
        })

        this.alarmThresholds = alarmValues
    }

    updateFromSeriesData(seriesData) {
        Object.keys(seriesData).forEach(key => {
            this[key] = seriesData[key]
        })
    }

    setPointData(data, addMode = REPLACE) {
        if (_.isNil(data)) {
            return
        }

        if (addMode === REPLACE) {

            this.am4Series.data = []
            this.backFillIntervals = []
        }

        let addModeString = addMode === REPLACE
                ? 'Replace'
                : (addMode === APPEND
                        ? 'Append'
                        : 'Prepend')

        data = this.processPointData(data)
        let firstReading = data[0]

        // In either case, log this data as loaded on the series data loader.
        this.seriesDataLoader.logLoadedInterval(firstReading.date, data[data.length - 1].date)

        // Find where to insert the data
        let indexToPlaceAt = this.am4Series.data.length
        for (let index = 0; index < this.am4Series.data.length; index++) {
            let testDate = this.am4Series.data[index].date
            if (testDate.getTime() > firstReading.date.getTime()) {
                indexToPlaceAt = index
                break
            }
        }

        let first = _.slice(this.am4Series.data, 0, indexToPlaceAt)
        let last = _.slice(this.am4Series.data, indexToPlaceAt, this.am4Series.data.length)
        let tmp = _.concat(
                first,
                data,
                last
        )

        this.am4Series.data = []
        this.am4Series.invalidateRawData()
        this.am4Series.addData(tmp)

        this.am4Series.baseInterval = this.dateAxis.baseInterval
        this.updateMinMax(tmp)

        // Only update axis intervals when all data has loaded to avoid
        // cascading regrouping during batch loading (each call triggers
        // datarangechanged → groupSeriesData for every series).
        if (!this.isLoadingData()) {
            this.dateAxis.updateDateAxisInterval()
        }
        this.dateAxis.updateGapAndMinBulletDistance()

        this.updateRender()
    }

    updateMinMax(data) {
        // Update min and max against the data, then against every threshold the
        // profile sets. A threshold has to stay inside the axis range whichever
        // side of the data it falls on, or its guide draws off the plot area and
        // the point looks like it has no thresholds -- ex. a profile with only
        // high thresholds (45 degF) on a point reading around 70 degF.
        this.min = _.minBy(data, 'value').value
        this.max = _.maxBy(data, 'value').value

        _.each(this.getThresholdValues(), value => {
            this.min = _.min([this.min, value])
            this.max = _.max([this.max, value])
        })

        // Check to see if the scalingProfile 'forceMinMax' has two entries
        // If so, take the first value as the min and the second as the max and compare against the set values; update if needed.
        // This helps for ex. CurrentProfileDigitalZeroClosed points that are always 0 or 1; without this
        // the min/max tends to be the same value and the y-axis text labels overlap each other.
        if (this.scalingProfile && this.scalingProfile.force_min_max && this.scalingProfile.force_min_max.length === 2){
            let forceMin = this.scalingProfile.force_min_max[0]
            let forceMax = this.scalingProfile.force_min_max[1]
            if (forceMin !== null && forceMin !== undefined && forceMin !== ''){
                this.min = _.min([this.min, forceMin])
            }
            if (forceMax !== null && forceMax !== undefined && forceMax !== ''){
                this.max = _.max([this.max, forceMax])
            }
        }

        // Update Side axis with the new series min/max
        this.sideAxis.updateMinMax()
    }

    processPointData(data) {
        let backFillStart = false
        _.forEach(data, (item, index) => {
            item.color = this.color
            item.backgroundColor = this.color
            item.date = new Date(item.date)
            let timestamp = item.date.getTime()
            if (this.validationReadingsMap[timestamp]) {
                item.backgroundColor = '#ff0000'
                
                // Enhance tooltip to indicate this is a validation alarm reading
                if (item.tooltip) {
                    const validationReading = this.validationReadingsMap[timestamp]
                    let userInfo = ''
                    if (validationReading.user) {
                        const displayName = validationReading.user.display_name || 'Unknown User'
                        const email = validationReading.user.email || 'No email'
                        userInfo = `\nInserted by: ${displayName} (${email})`
                    }
                    item.tooltip = item.tooltip + `\n\n🔴 Validation Alarm Reading\nThis data point was inserted via a Validation Alarm test.${userInfo}`
                }
            }

            // Check if we are done back filling and complete a range if so
            // if we are back-filling and this item is not backfilled, end the range
            if (backFillStart && !item.isBackfilled) {
                this.backFillIntervals.push({
                    start: _.clone(backFillStart),
                    end: _.clone(item.date),
                })

                backFillStart = false
            }
            // If we aren't back filling yet and the current item is backfilled.
            else if (!backFillStart && item.isBackfilled) {
                backFillStart = _.clone(item.date)
            }

            data[index] = item
        })

        // The we might have finished back filling
        if (backFillStart) {
            this.backFillIntervals.push({
                start: _.clone(backFillStart),
                end: _.clone(data[data.length - 1].date),
            })

            backFillStart = false
        }

        let lastReading = data[data.length - 1]
        if (lastReading.value === null) {
            // We reached the last point.
            data.pop()
        }
        else {
            // We do not have all the data. We need to load more.
            let start = _.clone(lastReading.date)
            this.seriesDataLoader.addInterval(start, this.end, true)
        }

        return data
    }

    getStartDate() {
        return new Date(this.am4Series.data[0].time)
    }

    getFirstReadingDate() {
        if (this.numData() === 0) {
            return
        }

        let reading = this.am4Series.data[0]
        return _.clone(reading.date)
    }

    getLastReadingDate() {
        if (this.numData() === 0) {
            return
        }

        let reading = this.am4Series.data[this.numData() - 1]
        return _.clone(reading.date)
    }

    loadData(start = null, end = null) {
        this.seriesDataLoader.addInterval(start, end)
    }

    isLoadingData() {
        return this.seriesDataLoader.intervalsToLoad.length > 0
    }

    numData() {
        return _.isNil(this.am4Series.data) ? 0 : this.am4Series.data.length
    }

    updateInterval(start, end) {
        // Nab the old interval so I can figure out how I need to adjust & Set the new interval
        let currentTime = new Date()
        let oldStart = _.clone(this.start)
        let oldEnd = _.clone(this.end)
        this.start = _.clone(start)
        this.end = _.clone(end)
        if (!this.isAm4SeriesReady()) {
            return
        }

        // If the start and end are the same, then there is no need to update the data
        let startChanged = oldStart.getTime() !== this.start.getTime()
        let endChanged = oldEnd.getTime() !== this.end.getTime()
        if (!startChanged && !endChanged) {
            return
        }

        // I only need to modify active data if the point is visible.  Otherwise, make sure the series data loader is clear
        if (! this.isVisible()) {
            this.am4Series.data = []
            this.backFillIntervals = []
            this.seriesDataLoader.reset()
            return
        }

        this.seriesDataLoader.clearQueue()

        let newStart = this.start.getTime()
        let newEnd = this.end.getTime()
        let oldStartTime = oldStart.getTime()
        let oldEndTime = oldEnd.getTime()
        // Remove Data No longer in the range
        // Determine if data should be removed from the beginning
        if (newStart > oldStartTime) {
            this.removeDataBetween(oldStart, this.start)
        }

        // Determine if data should be removed from the end
        if (newEnd < oldEndTime) {
            this.removeDataBetween(this.end, oldEnd)
        }

        // Add new data that should be in the range
        // Determine if Data needs to be added to the beginning of the series
        if (newStart < oldStartTime) {
            this.seriesDataLoader.addInterval(this.start, oldStart, PREPEND)
        }

        // Determine if Data needs to be added to the end of the series
        if (newEnd > oldEndTime) {
            this.seriesDataLoader.addInterval(oldEnd, this.end, APPEND)
        }

        this.setupBullets()
        this.updateBackFillIntervals()
    }

    removeDataBetween(start, end) {
        let data = this.am4Series.data

        // Find where we need to start deleting by finding where the date is larger than the start
        //   and stepping back 1
        let startTime = start.getTime()
        let startIndex = _.findIndex(data, item => item.date.getTime() >= startTime)
        // No point falls at or after the start of the removal window, so the
        // window (e.g. a new end that sits past the last loaded reading) covers
        // no data — keep everything. Without this guard findIndex's -1 turns
        // into startIndex 0 and the slice below wipes the entire series,
        // surfacing as a spurious "No data found in specified range".
        if (startIndex === -1) {
            return
        }
        startIndex = _.max([0, startIndex - 1])

        // Find where the deletion needs to end by finding where the point date is larger than the end
        //  and ending there
        let endTime = end.getTime()
        let endIndex = _.findIndex(data, item => item.date.getTime() > endTime)
        endIndex = endIndex === -1
            ? data.length
            : _.min([data.length, endIndex])


        // Conduct the slices and combine them to construct a new array for the data
        let newData = []
        newData = _.concat(_.slice(data, 0, startIndex), _.slice(data, endIndex))
        this.am4Series.data = newData
        if (! _.isNil(am4Settings.updateVueHook)) {
            am4Settings.updateVueHook()
        }

    }

    updateRender() {
        this.setupBullets()
        if (this.loadBackFillIntervals) {
            this.renderBackFillIntervals()
        }
        // Only dispatch datarangechanged when all data has loaded to avoid
        // redundant regrouping during batch loading.
        if (!this.isLoadingData()) {
            this.seriesChart.xAxis.get(0).dispatch('datarangechanged')
        }
    }

    updateBackFillIntervals() {
        this.backFillIntervals = _.filter(this.backFillIntervals, interval => {
            // Ensure that the only remaining intervals are within the series show interval
            return interval.start.getTime() < this.end.getTime() && interval.end.getTime() > this.start.getTime()
        })
    }

    renderBackFillIntervals(scrollbar = null) {
        if (!_.isNil(scrollbar) && scrollbar.scrollbarChart.xAxes.length > 0) {
            this.scrollAxis = scrollbar.scrollbarChart.xAxes.getIndex(0)
        }
        else {
            this.scrollAxis = this.dateAxis.getScrollAxisIfExists()
        }

        if (_.isNil(this.scrollAxis)) {
            return
        }

        // If we should not load backfill intervals then clear the axis ranges and return
        if (! this.loadBackFillIntervals) {
            this.clearBackFillIntervals()
            return
        }

        this.scrollAxis.axisRanges.clear()
        _.forEach(this.backFillIntervals, interval => {
            let range = this.scrollAxis.axisRanges.create()
            range.axisFill.fillOpacity = 1
            range.strokeOpacity = 1
            range.date = interval.start
            range.endDate = interval.end
            range.axisFill.fill = am4Settings.BACK_FILL_COLOR
        })
    }

    clearBackFillIntervals() {
        this.scrollAxis.axisRanges.clear()
    }

    getBackFillIntervals() {
        return this.backFillIntervals
    }

    hasBackFillIntervals() {
        return this.backFillIntervals.length > 0
    }

    isSetupValid() {
        return !(
                _.isNil(this.seriesChart)
        );
    }

    setAlarmInfo(alarmInfo) {
        this.alarmInfo = alarmInfo
    }

    setValidationReadings(readings) {
        this.validationReadingsMap = {}
        if (!Array.isArray(readings)) {
            return
        }
        readings.forEach(r => {
            if (r.time) {
                let timestamp = new Date(r.time).getTime()
                this.validationReadingsMap[timestamp] = r
            }
        })
    }

    getAlarmInfo() {
        if (_.isNil(this.alarmInfo)) {
            return {
                alarm: {},
                caution: {},
            }
        }

        return this.alarmInfo
    }

    getThresholdValues() {
        return _.filter([
            this.alarmThresholds.lowAlarmValue,
            this.alarmThresholds.lowCautionValue,
            this.alarmThresholds.highCautionValue,
            this.alarmThresholds.highAlarmValue,
        ], value => !_.isNil(value))
    }

    create(company, chart, id, sideAxis, scalingProfile, start = null, end = null, axisPosition = am4Settings.AXIS_LEFT, loadBackFillIntervals = false) {
        this.requestNumber = 0
        this.company = company
        this.min = 100000
        this.max = -100000
        this.lastUpdateTime = null
        this.alarmInfo = null
        this.start = _.isNil(start) ? new Date() : _.clone(start)
        this.end = _.isNil(end) ? new Date() : _.clone(end)
        if (_.isNil(start)) {
            this.start.setDate(this.end.getDate() - 1)
        }
        this.scalingProfile = scalingProfile
        this.am4ThresholdAxisRanges = []
        this.alarmThresholds = {}

        this.visible = false
        this.seriesChart = chart
        this.color = SeriesColor.DEFAULT_COLOR

        this.id = id
        this.axisPosition = axisPosition
        this.sideAxis = sideAxis
        this.dateAxis = null
        this.scrollAxis = null
        this.seriesDataLoader = new SeriesDataLoader(this)
        this.backFillIntervals = []
        this.loadBackFillIntervals = loadBackFillIntervals
        this.validationReadingsMap = {}
    }

    updateLoadingBackFillIntervals(loadBackFillIntervals) {
        this.loadBackFillIntervals = loadBackFillIntervals
    }

    setupGroupingAdapter() {
        this.am4Series.adapter.add("groupDataItem", val => {
            val[`${this.id}_tooltip`] = val.dataItem.dataContext.tooltip

            let min = 1000000
            let max = -1000000
            let mean = 0

            val.dataItem.groupDataItems.forEach(subVal => {
                let v = subVal.valueY
                min = Math.min(min, v)
                max = Math.max(max, v)
                mean += v
            })
            if (val.dataItem.groupDataItems.length > 0) {
                mean /= val.dataItem.groupDataItems.length
            }
            let thisMax = max
            let thisMin = min

            if (thisMax > this.alarmThresholds.highAlarmValue){
                val[`value`] = thisMax;
            } else if (thisMin < this.alarmThresholds.lowAlarmValue){
                val[`value`] = thisMin;
            } else if (thisMax > this.alarmThresholds.highCautionValue){
                val[`value`] = thisMax;
            } else if (thisMin < this.alarmThresholds.lowCautionValue){
                val[`value`] = thisMin;
            } else {
                val[`value`] = mean
            }

            return val;
        })
    }

    isAm4SeriesReady() {
        return !(_.isNil(this.am4Series) || this.am4Series.isDisposed())
    }

    dispose() {
        if (!_.isNil(this.am4Series)) {
            this.am4Series.dispose()
            this.am4Series = null
            this.sideAxis = null


            SeriesColor.releaseColor(this.color)
        }
        this.seriesDataLoader.stop()
    }
}